import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { BookOpen, Loader2, Layers, ArrowLeft, Lock, CheckCircle, Copy, QrCode, ExternalLink, Clock } from 'lucide-react';
import { toast } from 'sonner';
import { QRCodeSVG } from 'qrcode.react';
import { PublicLayout } from '../../app/layouts/PublicLayout';
import { fetchPublicCourse, fetchMyCourses, formatPrice } from '../../features/courses/api/courses';
import { createOrder, payOrder, fetchPaymentMethods, checkOrderStatus, DuitkuMethod, PayResult } from '../../features/orders/api/orders';
import { useAuth } from '../../features/auth/AuthContext';
import type { Course } from '../../types';
import { COURSE, ORDER } from '../../constants/testIds';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../../components/ui/dialog';
import { Countdown } from '../../components/common/Countdown';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const categoryOf = (m: DuitkuMethod) => {
  const n = (m.paymentName || '').toUpperCase();
  if (n.includes('QRIS')) return 'QRIS';
  if (n.includes('VA')) return 'Virtual Account';
  if (n.includes('CREDIT CARD')) return 'Kartu Kredit';
  if (n.includes('RETAIL') || n.includes('INDOMARET') || n.includes('ALFA')) return 'Gerai Retail';
  return 'E-Wallet & Lainnya';
};
const GROUP_ORDER = ['Virtual Account', 'E-Wallet & Lainnya', 'QRIS', 'Kartu Kredit', 'Gerai Retail'];

export default function CourseDetailPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [course, setCourse] = useState<Course | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [owned, setOwned] = useState(false);
  const [buying, setBuying] = useState(false);
  const [methodOpen, setMethodOpen] = useState(false);
  const [methods, setMethods] = useState<DuitkuMethod[]>([]);
  const [pendingOrderId, setPendingOrderId] = useState('');
  const [selectedMethod, setSelectedMethod] = useState('');
  const [payInfo, setPayInfo] = useState<PayResult | null>(null);
  const [payOpen, setPayOpen] = useState(false);
  const [paid, setPaid] = useState(false);

  useEffect(() => {
    if (!slug) return;
    fetchPublicCourse(slug)
      .then(setCourse)
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  }, [slug]);

  useEffect(() => {
    if (!user || !course) return;
    fetchMyCourses()
      .then((mine) => setOwned(mine.some((c) => c.id === course.id)))
      .catch(() => {});
  }, [user, course]);

  const redirectTo = (raw: string) => {
    let url = raw || '';
    if (url && !url.startsWith('http')) url = `${BACKEND_URL}${url}`;
    if (url) {
      window.location.href = url;
    } else {
      toast.error('Checkout URL tidak tersedia');
      setBuying(false);
    }
  };

  const handleBuy = async () => {
    if (!course) return;
    if (!user) {
      toast.info('Silakan masuk terlebih dahulu untuk membeli course.');
      navigate('/');
      return;
    }
    setBuying(true);
    try {
      const order = await createOrder({ product_type: 'course', product_id: course.id });
      const res = await fetchPaymentMethods(course.price).catch(() => ({ mode: 'mock', methods: [], message: '' }));
      const real = res.methods.filter((m) => m.paymentMethod !== 'MOCK');
      if (res.mode === 'duitku' && real.length > 0) {
        setPendingOrderId(order.id);
        setMethods(real);
        setSelectedMethod(real[0].paymentMethod);
        setMethodOpen(true);
        setBuying(false);
      } else {
        const pay = await payOrder(order.id);
        redirectTo(pay.checkout_url);
      }
    } catch (e: any) {
      const detail = e.response?.data?.detail;
      const msg = typeof detail === 'string' ? detail : '';
      if (e.response?.status === 400 && /memiliki|own/i.test(msg)) {
        toast.info(msg || 'Anda sudah memiliki course ini.');
        setOwned(true);
      } else {
        toast.error('Gagal memproses pembelian', { description: msg || e.message });
      }
      setBuying(false);
    }
  };

  const confirmPay = async () => {
    setBuying(true);
    try {
      const pay = await payOrder(pendingOrderId, selectedMethod);
      const va = pay.payment?.va_number;
      const qr = pay.payment?.qr_string;
      if (va || qr) {
        setPayInfo(pay);
        setMethodOpen(false);
        setPaid(false);
        setPayOpen(true);
        setBuying(false);
      } else {
        redirectTo(pay.checkout_url);
      }
    } catch (e: any) {
      toast.error('Gagal memproses pembayaran', { description: e.response?.data?.detail || e.message });
      setBuying(false);
    }
  };

  // Poll order status while the payment instruction dialog is open
  useEffect(() => {
    if (!payOpen || !pendingOrderId || paid) return;
    const id = setInterval(async () => {
      try {
        const o = await checkOrderStatus(pendingOrderId);
        if (o.status === 'paid') {
          setPaid(true);
          setOwned(true);
          toast.success('Pembayaran diterima!', { description: 'Course Anda kini aktif.' });
          clearInterval(id);
        }
      } catch {}
    }, 6000);
    return () => clearInterval(id);
  }, [payOpen, pendingOrderId, paid]);

  const copyText = (t: string) => { navigator.clipboard.writeText(t); toast.success('Disalin'); };

  return (
    <PublicLayout>
      <Link to="/courses" className="inline-flex items-center gap-2 text-white/60 hover:text-cyan-400 mb-8 text-sm transition-colors">
        <ArrowLeft className="w-4 h-4" strokeWidth={1.5} /> Kembali ke katalog
      </Link>

      {loading ? (
        <div className="flex justify-center py-20">
          <Loader2 className="w-8 h-8 text-cyan-400 animate-spin" strokeWidth={1.5} />
        </div>
      ) : notFound || !course ? (
        <div className="text-white/50 py-20 text-center">Course tidak ditemukan.</div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8" data-testid={COURSE.publicDetail}>
          <div className="lg:col-span-2">
            <div className="h-64 rounded-2xl bg-gradient-to-br from-cyan-400/20 to-transparent border border-white/10 flex items-center justify-center overflow-hidden mb-6">
              {course.thumbnail_url ? (
                <img src={course.thumbnail_url} alt={course.title} className="w-full h-full object-cover" />
              ) : (
                <BookOpen className="w-14 h-14 text-cyan-400/60" strokeWidth={1.5} />
              )}
            </div>
            <span className="text-[10px] uppercase tracking-[0.2em] text-cyan-400">{course.category}</span>
            <h1 className="text-3xl sm:text-4xl tracking-tight font-light text-white mt-2 mb-4">{course.title}</h1>
            <p className="text-white/70 leading-relaxed whitespace-pre-line">{course.description}</p>
          </div>

          <div className="lg:col-span-1">
            <div className="bg-white/[0.03] backdrop-blur-2xl border border-white/10 rounded-2xl p-6 sticky top-24">
              <p className="text-3xl font-light text-cyan-400 mb-2">{formatPrice(course.price, course.currency)}</p>
              <div className="flex items-center gap-2 text-sm text-white/50 mb-6">
                <Layers className="w-4 h-4" strokeWidth={1.5} />
                {course.lesson_count ?? 0} lesson
              </div>
              {owned ? (
                <>
                  <button
                    data-testid={ORDER.ownedBadge}
                    onClick={() => navigate('/member/courses')}
                    className="w-full bg-emerald-400/15 text-emerald-300 border border-emerald-400/30 font-medium px-6 py-3 rounded-lg transition-colors hover:bg-emerald-400/25 flex items-center justify-center gap-2"
                  >
                    <CheckCircle className="w-4 h-4" strokeWidth={2} /> Sudah Dimiliki — Buka
                  </button>
                  <div className="flex items-center gap-2 text-xs text-emerald-400/70 mt-4 justify-center">
                    <CheckCircle className="w-3.5 h-3.5" strokeWidth={1.5} />
                    Anda memiliki akses course ini
                  </div>
                </>
              ) : (
                <>
                  <button
                    data-testid={COURSE.buyCta}
                    onClick={handleBuy}
                    disabled={buying}
                    className="w-full bg-cyan-400 text-[#050914] font-medium px-6 py-3 rounded-lg transition-colors hover:bg-cyan-300 disabled:opacity-60 flex items-center justify-center gap-2"
                  >
                    {buying && <Loader2 className="w-4 h-4 animate-spin" strokeWidth={2} />}
                    Beli Course
                  </button>
                  <div className="flex items-center gap-2 text-xs text-white/40 mt-4 justify-center">
                    <Lock className="w-3.5 h-3.5" strokeWidth={1.5} />
                    Akses lesson setelah pembelian
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      <Dialog open={methodOpen} onOpenChange={setMethodOpen}>
        <DialogContent className="bg-[#090D1A] border-white/10 text-white max-w-md max-h-[80vh] overflow-y-auto" data-testid="payment-method-dialog">
          <DialogHeader>
            <DialogTitle>Pilih Metode Pembayaran</DialogTitle>
            <DialogDescription className="text-white/50">Pilih channel pembayaran sebelum lanjut ke Duitku.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            {GROUP_ORDER.map((group) => {
              const inGroup = methods.filter((m) => categoryOf(m) === group);
              if (inGroup.length === 0) return null;
              return (
                <div key={group}>
                  <p className="text-[11px] uppercase tracking-wider text-white/40 mb-2">{group}</p>
                  <div className="space-y-2">
                    {inGroup.map((m) => (
                      <button
                        key={m.paymentMethod}
                        data-testid={`payment-method-${m.paymentMethod}`}
                        onClick={() => setSelectedMethod(m.paymentMethod)}
                        className={`w-full flex items-center gap-3 rounded-xl p-3 border transition-colors text-left ${selectedMethod === m.paymentMethod ? 'border-cyan-400 bg-cyan-400/10' : 'border-white/10 hover:border-white/30'}`}
                      >
                        {m.paymentImage ? (
                          <img src={m.paymentImage} alt={m.paymentName} className="w-10 h-7 object-contain bg-white rounded p-0.5" />
                        ) : (
                          <div className="w-10 h-7 rounded bg-white/10" />
                        )}
                        <div className="flex-1 min-w-0">
                          <p className="text-sm text-white truncate">{m.paymentName}</p>
                          {Number(m.totalFee) > 0 && <p className="text-[11px] text-white/40">Biaya: Rp{m.totalFee}</p>}
                        </div>
                        {selectedMethod === m.paymentMethod && <span className="w-2.5 h-2.5 rounded-full bg-cyan-400" />}
                      </button>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
          <button
            data-testid="payment-method-confirm"
            onClick={confirmPay}
            disabled={buying || !selectedMethod}
            className="mt-2 w-full bg-cyan-400 text-[#050914] font-medium px-6 py-3 rounded-lg transition-colors hover:bg-cyan-300 disabled:opacity-60 flex items-center justify-center gap-2"
          >
            {buying && <Loader2 className="w-4 h-4 animate-spin" strokeWidth={2} />}
            Lanjut Bayar
          </button>
        </DialogContent>
      </Dialog>

      <Dialog open={payOpen} onOpenChange={setPayOpen}>
        <DialogContent className="bg-[#090D1A] border-white/10 text-white max-w-md max-h-[85vh] overflow-y-auto" data-testid="payment-instruction-dialog">
          <DialogHeader>
            <DialogTitle>{paid ? 'Pembayaran Berhasil' : 'Instruksi Pembayaran'}</DialogTitle>
            <DialogDescription className="text-white/50">
              {paid ? 'Terima kasih! Course Anda kini aktif.' : 'Selesaikan pembayaran di bawah ini. Status akan diperbarui otomatis.'}
            </DialogDescription>
          </DialogHeader>

          {paid ? (
            <div className="py-6 text-center space-y-4">
              <CheckCircle className="w-14 h-14 text-emerald-400 mx-auto" strokeWidth={1.5} />
              <button data-testid="payment-open-course" onClick={() => navigate('/member/courses')} className="w-full bg-cyan-400 text-[#050914] font-medium px-6 py-3 rounded-lg hover:bg-cyan-300 transition-colors">
                Buka My Courses
              </button>
            </div>
          ) : (
            <div className="space-y-4">
              {payInfo?.payment?.amount != null && (
                <div className="flex items-center justify-between bg-white/[0.03] border border-white/10 rounded-lg px-4 py-3">
                  <span className="text-xs text-white/50">Total</span>
                  <span className="text-cyan-400 font-medium">{formatPrice(payInfo.payment.amount, payInfo.payment.currency)}</span>
                </div>
              )}

              {payInfo?.payment?.qr_string ? (
                <div className="flex flex-col items-center gap-3">
                  <div className="flex items-center gap-2 text-sm text-white/70"><QrCode className="w-4 h-4 text-cyan-400" strokeWidth={1.5} /> Scan QRIS</div>
                  <div className="bg-white p-3 rounded-xl" data-testid="payment-qris">
                    <QRCodeSVG value={payInfo.payment.qr_string} size={200} />
                  </div>
                  <p className="text-[11px] text-white/40 text-center">Buka aplikasi e-wallet / m-banking Anda, pilih QRIS, lalu scan kode di atas.</p>
                </div>
              ) : payInfo?.payment?.va_number ? (
                <div>
                  <p className="text-xs uppercase tracking-wider text-white/50 mb-1.5">Nomor Virtual Account</p>
                  <div className="flex items-center gap-2">
                    <div data-testid="payment-va-number" className="flex-1 bg-white/[0.03] border border-white/10 rounded-lg px-4 py-3 font-mono text-lg tracking-wide text-white">
                      {payInfo.payment.va_number}
                    </div>
                    <button data-testid="payment-copy-va" onClick={() => copyText(payInfo!.payment.va_number!)} className="p-3 rounded-lg border border-white/10 text-white/60 hover:text-cyan-400 hover:bg-white/5">
                      <Copy className="w-4 h-4" strokeWidth={1.5} />
                    </button>
                  </div>
                  <p className="text-[11px] text-white/40 mt-2">Transfer ke nomor VA di atas via m-banking / ATM. Akses course otomatis aktif setelah pembayaran.</p>
                </div>
              ) : null}

              <div className="flex items-center justify-center gap-2 text-xs text-white/50 pt-1">
                <Loader2 className="w-3.5 h-3.5 animate-spin text-cyan-400" strokeWidth={2} /> Menunggu pembayaran…
              </div>

              {payInfo?.payment?.expired_at && (
                <div className="flex items-center justify-center gap-2 text-xs text-amber-300">
                  <Clock className="w-3.5 h-3.5" strokeWidth={1.5} /> Bayar sebelum <Countdown expiresAt={payInfo.payment.expired_at} />
                </div>
              )}

              {payInfo?.checkout_url && (
                <a href={payInfo.checkout_url} target="_blank" rel="noreferrer" data-testid="payment-open-duitku" className="flex items-center justify-center gap-2 text-sm text-cyan-400 hover:text-cyan-300">
                  <ExternalLink className="w-4 h-4" strokeWidth={1.5} /> Buka halaman pembayaran Duitku
                </a>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </PublicLayout>
  );
}
